// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { QualifyingStatusProduct } from '@iheartjane/data-access';
import {
  useRemoveCartProduct as useV3RemoveCartProduct,
  useUpdateCartProduct as useV3UpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { productPhotoMetadata } from '@iheartjane/menu';
import { AddIcon, Image, TrashIcon } from '@iheartjane/reefer';
import sumBy from 'lodash-es/sumBy';
import take from 'lodash-es/take';
import type { ReactElement } from 'react';

import {
  getLocalCartUuid,
  useRemoveCartProduct,
  useUpdateCartProduct,
} from '@jane/kiosk/data-access';
import { useKioskSpacing } from '@jane/kiosk/hooks';
import { useProductTracking } from '@jane/kiosk/providers';

const BundleProductContainer = styled.div<{
  borderColor?: string;
  hasImage?: boolean;
}>(({ hasImage, borderColor = 'rgba(0,0,0,.1)' }) => {
  const { isIpad } = useKioskSpacing();
  const isGrey = borderColor === 'rgba(0,0,0,.1)';

  return {
    background: hasImage ? 'white' : 'none',
    position: 'relative' as const,
    width: isIpad ? 104 : 136,
    height: isIpad ? 104 : 136,
    borderRadius: 16,
    border: isGrey ? `2px solid ${borderColor}` : `4px solid ${borderColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    '&:not(:last-child)': {
      marginRight: isIpad ? 24 : 32,
    },
  };
});

const DismissIconContainer = styled.div(() => {
  const { isIpad } = useKioskSpacing();

  return {
    position: 'absolute' as const,
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: isIpad ? -8 : -16,
    right: isIpad ? -8 : -16,
    width: isIpad ? 32 : 48,
    height: isIpad ? 32 : 48,
    borderRadius: 24,
    cursor: 'pointer',
    zIndex: 1,
    boxShadow: '0px 2px 16px rgb(0 0 0 / 5%), 0px 0px 0px 1px rgb(0 0 0 / 5%)',
  };
});

type BundleProduct = QualifyingStatusProduct & {
  discountableIsAlsoQualifier?: boolean;
};

type GlobalProductCount = {
  [key: string]: number;
};

interface BundleProductCardsProps {
  globalProductCount: GlobalProductCount;
  perfectMixAndMatch: boolean;
  products: BundleProduct[];
  productType: string;
  qualifyingStatus: string;
  threshold: number;
}

export const BundleProductCards = ({
  products = [],
  threshold,
  productType,
  qualifyingStatus,
  globalProductCount = {},
  perfectMixAndMatch,
}: BundleProductCardsProps) => {
  const theme = useTheme();
  const { isIpad } = useKioskSpacing();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);

  const productCount = sumBy(products, 'count');
  const cartUuid = getLocalCartUuid();

  const { mutate: removeCartProduct } = useRemoveCartProduct({});
  const { mutate: updateProduct } = useUpdateCartProduct({});
  const { mutate: v3RemoveCartProduct } = useV3RemoveCartProduct({});
  const { mutate: v3UpdateProduct } = useV3UpdateCartProduct({});
  const {
    cartToppers: { removeCartTopperId },
    inlineAds: { removeInlineAdId },
  } = useProductTracking();

  if (
    productType === 'discounted' &&
    perfectMixAndMatch &&
    productCount === 0
  ) {
    return null;
  }

  let productCards: ReactElement[] = products.reduce(
    (acc: ReactElement[], product: BundleProduct) => {
      const { id, count, price_id, name, discountableIsAlsoQualifier } =
        product;
      const { url: photoUrl } = productPhotoMetadata(product);

      if (discountableIsAlsoQualifier) {
        return acc;
      }

      for (let i = 0; i < count; ++i) {
        acc.push(
          <BundleProductContainer
            key={`${productType}-${id}-${price_id}-${i}`}
            hasImage
            data-testid={`bundle-${productType}-product`}
          >
            <DismissIconContainer
              onClick={() => {
                const productGlobalCount =
                  globalProductCount[`${product.id}:${product.price_id}`];

                if (productGlobalCount && productGlobalCount > 1) {
                  if (useV3Cart) {
                    v3UpdateProduct({
                      appMode: 'kiosk',
                      cartUuid: String(cartUuid),
                      productId: product.product_id ?? product.id,
                      count: productGlobalCount - 1,
                      priceId: product.price_id,
                    });
                  } else {
                    updateProduct({
                      cart_uuid: String(cartUuid),
                      product: {
                        count: productGlobalCount - 1,
                        price_id: product.price_id,
                        product_id: product.product_id ?? product.id,
                      },
                    });
                  }
                } else if (useV3Cart) {
                  v3RemoveCartProduct(
                    {
                      appMode: 'kiosk',
                      cartUuid: String(cartUuid),
                      productId: product.product_id ?? product.id,
                      priceId: product.price_id,
                    },
                    {
                      onSuccess: () => {
                        removeInlineAdId(product.id);
                        removeCartTopperId(product.id);
                      },
                    }
                  );
                } else {
                  removeCartProduct(
                    {
                      cart_uuid: cartUuid,
                      product_id: product.id,
                    },
                    {
                      onSuccess: () => {
                        removeInlineAdId(product.id);
                        removeCartTopperId(product.id);
                      },
                    }
                  );
                }
              }}
            >
              <TrashIcon size={isIpad ? 'sm' : 'md'} />
            </DismissIconContainer>
            <Image
              src={photoUrl}
              altText={name}
              objectFit="cover"
              borderRadius="14px"
              height="100%"
              responsive
              sizes="card"
            />
          </BundleProductContainer>
        );
      }

      return acc;
    },
    []
  );

  productCards = take(productCards, threshold);

  const missingProducts = threshold - productCount;

  if (missingProducts > 0) {
    for (let i = 0; i < missingProducts; i++) {
      if (productType === 'qualified') {
        const isActive = i === 0;
        const borderColor = isActive
          ? theme.colors.brand.purple.main
          : 'rgba(0,0,0,.1)';

        productCards.push(
          <BundleProductContainer
            borderColor={borderColor}
            key={`add-${i}`}
            data-testid={`bundle-${productType}-product`}
          >
            <AddIcon size="xl" color={isActive ? 'purple' : 'grays-mid'} />
          </BundleProductContainer>
        );
      } else {
        const isActive =
          qualifyingStatus === 'qualified' || qualifyingStatus === 'applied';
        const borderColor = isActive
          ? theme.colors.brand.sunset.main
          : 'rgba(0,0,0,.1)';

        productCards.push(
          <BundleProductContainer
            borderColor={borderColor}
            key={`tag-${i}`}
            data-testid={`bundle-${productType}-product`}
          >
            <AddIcon size="xl" color={isActive ? 'sunset' : 'grays-mid'} />
          </BundleProductContainer>
        );
      }
    }
  }

  return <>{productCards}</>;
};
